/**
 * Project-finance feature flags + sourced default assumptions.
 *
 * H-5 / FA_WC_DECOMM_CASHFLOWS (PART H of the non-EO backlog). This module
 * OWNS the flag and the named, sourced assumptions for two project-finance
 * cashflows that the legacy LCOE/NPV chain (`src/lib/projectFinance.ts`)
 * otherwise omits:
 *
 *   1. WORKING-CAPITAL lock-up — cash tied up early (a fraction of annual
 *      revenue), RELEASED (recovered) in the final operating year. A pure
 *      timing effect on NPV (you get the money back, but later and discounted);
 *      a small upward push on LCOE.
 *   2. DECOMMISSIONING — a terminal-year cost at end of design life
 *      (optionally salvage-netted by the caller before it reaches here).
 *
 * Both flow into the discounted cashflows feeding NPV + LCOE + IRR + payback.
 *
 * CURRENT STATE (verified 2026-08-19): this flag is **ON IN PRODUCTION** —
 * `cloudbuild.yaml` sets `_FA_WC_DECOMM: 'true'` (browser bundle) and
 * `ops/manifests/jobs/report-generation.yaml` sets
 * `FA_WC_DECOMM_CASHFLOWS: 'true'` (report-generation job), enabled 2026-06-22.
 * The code default is OFF, so local/test runs are unflagged unless they opt in;
 * do NOT read "default off" as "off in prod".
 *
 * REVERSIBILITY (acceptance criterion): with the flag OFF the finance helpers
 * reproduce the pre-flag numbers BYTE-IDENTICALLY (no WC, no decommissioning
 * flow). The flag only ADDS the two
 * cashflows; it never changes the existing CAPEX / OPEX / revenue math. The
 * flip is a Cloud Run env change, zero redeploy — same rollback story as
 * `generationTier.ts` / `incrementalReport.ts` / `modelTiering.ts`.
 *
 * SIGN CONVENTION (per CLAUDE.md §2 finance): positive = inflow, costs
 * negative. Working capital is an outflow (negative) early and an inflow
 * (positive) at recovery; decommissioning is an outflow (negative) at EOL.
 * The cashflow builder applies these signs — this module only supplies the
 * magnitudes + the gate.
 */

// ── Flag plumbing ───────────────────────────────────────────────────────────

/** Browser bundle reads the `VITE_`-prefixed var; Node/server reads the bare. */
const FLAG = 'VITE_FA_WC_DECOMM_CASHFLOWS';
const FLAG_SERVER = 'FA_WC_DECOMM_CASHFLOWS';

/**
 * Read a string env var with the `import.meta.env` → `process.env` precedence
 * used elsewhere in the repo (mirrors `incrementalReport.ts` /
 * `incrementalSed.ts` / `modelTiering.ts`). Never throws — `import.meta` is
 * unavailable in some Node/test contexts, so we fall through to `process.env`.
 */
function readEnvVar(name: string): string | undefined {
  try {
    if (typeof import.meta !== 'undefined' && (import.meta as { env?: unknown }).env != null) {
      const v = (import.meta as { env: Record<string, unknown> }).env[name];
      if (typeof v === 'string') return v;
    }
  } catch {
    /* import.meta unavailable in older Node — fall through to process.env */
  }
  if (typeof process !== 'undefined' && process.env) {
    return process.env[name];
  }
  return undefined;
}

/** Case-insensitive `'true'` / `'1'` truthiness — the repo-wide flag rule. */
function isTruthy(raw: string | undefined): boolean {
  if (typeof raw !== 'string') return false;
  const v = raw.trim().toLowerCase();
  return v === 'true' || v === '1';
}

/** Test-only override (set/clear in `afterEach`). `undefined` ⇒ read env. */
let wcDecommOverride: boolean | undefined;

/**
 * Whether the working-capital + decommissioning cashflows are enabled.
 * Code default OFF (flag-off is byte-identical to the pre-flag engine), but
 * **ON in production** — see the module header for where it is set.
 *
 * Enable in prod with a Cloud Run env change (no redeploy):
 *   gcloud run services update datacrate-platform \
 *     --update-env-vars=FA_WC_DECOMM_CASHFLOWS=true   (server)
 * The browser bundle needs the build-time `VITE_FA_WC_DECOMM_CASHFLOWS=true`
 * ARG (owned by the cloudbuild/Dockerfile agent — NOT edited here).
 */
export function isWcDecommCashflowsEnabled(): boolean {
  if (wcDecommOverride !== undefined) return wcDecommOverride;
  return isTruthy(readEnvVar(FLAG)) || isTruthy(readEnvVar(FLAG_SERVER));
}

/** Test-only setter for {@link isWcDecommCashflowsEnabled}. */
export function setWcDecommCashflowsForTests(value: boolean | undefined): void {
  wcDecommOverride = value;
}

// ── Named, sourced default assumptions (no magic numbers) ────────────────────
//
// These defaults apply ONLY when the flag is ON and the caller has NOT supplied
// an explicit value (extracted from an uploaded model or entered by the user).
// An explicit value ALWAYS wins — the defaults exist so the feature still moves
// the numbers for projects that have not itemised WC / decommissioning, rather
// than silently contributing $0.

/**
 * Working-capital lock-up as a FRACTION OF YEAR-ONE REVENUE.
 *
 * Basis/source: project-finance convention sizes net working capital from
 * operating turnover (receivables + inventory − payables). For a contracted
 * power asset selling under a PPA, ~2 months (≈ 60 / 365 ≈ 16.7%) of annual
 * revenue is a standard planning figure (e.g. IRENA "Renewable Power
 * Generation Costs" project-finance assumptions; Yescombe, *Principles of
 * Project Finance*, working-capital sizing). We adopt 60 days = 0.1667 of
 * year-one revenue as the documented default. Override per project via the
 * explicit `workingCapitalUsd` input.
 */
export const DEFAULT_WORKING_CAPITAL_DAYS_OF_REVENUE = 60;
export const DAYS_PER_YEAR = 365;
export const DEFAULT_WORKING_CAPITAL_FRACTION_OF_REVENUE =
  DEFAULT_WORKING_CAPITAL_DAYS_OF_REVENUE / DAYS_PER_YEAR;

/**
 * Decommissioning cost as a FRACTION OF TOTAL CAPEX.
 *
 * Basis/source: for thermal / combined-cycle plant, end-of-life dismantling
 * and site remediation is commonly budgeted at ~5% of overnight capital cost
 * (matches the existing finDecision gas/SMR presets' `decommPctOfCapex = 0.05`
 * — NREL ATB / EIA AEO derived). We adopt 5% as the documented default so the
 * legacy `FinancialAssumptions` model is consistent with the pathway presets.
 * Nuclear and other technologies with statutory decommissioning funds should
 * pass an explicit `decommissioningUsd`. Override per project via the explicit
 * `decommissioningUsd` input.
 */
export const DEFAULT_DECOMMISSIONING_FRACTION_OF_CAPEX = 0.05;

/**
 * Resolve the EFFECTIVE working-capital and decommissioning magnitudes (USD,
 * positive) for a project, given its core inputs.
 *
 * Rules:
 *   - Flag OFF ⇒ both 0 (the finance helpers add nothing — byte-identical).
 *   - Flag ON, explicit value present (≥ 0) ⇒ use it verbatim.
 *   - Flag ON, value absent (null/undefined) ⇒ apply the sourced default
 *     fraction × basis.
 *   - Flag ON, value explicitly 0 ⇒ honour the 0 (the project opted out of
 *     that component — a 0 is a real input, not "missing").
 *
 * `now`-independent and pure. Returns positive magnitudes; the cashflow builder
 * applies the sign convention.
 */
export function resolveWcDecommUsd(args: {
  /** Explicit working-capital lock-up (USD), or null when not supplied. */
  workingCapitalUsd: number | null | undefined;
  /** Explicit decommissioning cost (USD), or null when not supplied. */
  decommissioningUsd: number | null | undefined;
  /** Year-one revenue (USD) — the basis for the WC default. */
  yearOneRevenueUsd: number | null | undefined;
  /** Total CAPEX (USD) — the basis for the decommissioning default. */
  capexTotalUsd: number | null | undefined;
}): { workingCapitalUsd: number; decommissioningUsd: number } {
  if (!isWcDecommCashflowsEnabled()) {
    return { workingCapitalUsd: 0, decommissioningUsd: 0 };
  }

  const wc = resolveComponent(
    args.workingCapitalUsd,
    args.yearOneRevenueUsd,
    DEFAULT_WORKING_CAPITAL_FRACTION_OF_REVENUE,
  );
  const decomm = resolveComponent(
    args.decommissioningUsd,
    args.capexTotalUsd,
    DEFAULT_DECOMMISSIONING_FRACTION_OF_CAPEX,
  );
  return { workingCapitalUsd: wc, decommissioningUsd: decomm };
}

/**
 * Explicit value wins (incl. an explicit 0). When absent (null/undefined), fall
 * back to `fraction × basis` if the basis is a finite positive number; else 0.
 */
function resolveComponent(
  explicit: number | null | undefined,
  basis: number | null | undefined,
  fraction: number,
): number {
  if (explicit != null && Number.isFinite(explicit)) {
    return Math.max(0, explicit);
  }
  if (basis != null && Number.isFinite(basis) && basis > 0) {
    return basis * fraction;
  }
  return 0;
}
